import { hasUrlFeatureFlagTrain } from '../../components/molecules/URLFeatureFlag/URLFeatureFlag'
import config, { Environment } from '../../config/config'
import { Attempt, Institution, UserProfile } from '../../models/global.types'

export type CertificateVariant = 'enterprise' | 'd2c'
export type CertificateYear = 2025 | 2026

export const CERT_2026_CUTOFF_MS = Date.parse('2026-01-01T00:00:00.000Z')

export const OVERLAY_NAVY: [number, number, number] = [15, 52, 67]
export const OVERLAY_DARK_GRAY: [number, number, number] = [16, 17, 17]
export const NAME_MINT_SHADOW: [number, number, number] = [153, 243, 207]

// Letterbox page in jsPDF px units, rotated to landscape by generateCertificatePdf.
export const PDF_PAGE_SHORT_SIDE = 612
export const PDF_PAGE_LONG_SIDE = 792

export const PDF_NAME_FONT_SIZE = 48
export const PDF_TEXT_FONT_SIZE = 28
export const PDF_INSTITUTION_FONT_SIZE = 22
export const PDF_NAME_SHADOW_OFFSET = 2
export const PDF_NAME_MIN_FONT_SIZE = 28
// Inner white band is ~82% of the artwork. Stay inside that so long names
// shrink (and wrap only if they still overflow at the minimum size).
export const PDF_NAME_MAX_WIDTH_PCT = 78

export type MeasureTextWidth = (text: string, fontSize: number) => number

export interface FitCertificateNameOptions {
  measureWidth?: MeasureTextWidth
  pageWidth?: number
}

// Adobe Helvetica-Bold AFM widths in 1/1000 em. Preview uses these; the PDF
// measures with jsPDF getTextWidth so both are Helvetica-Bold, not an average.
const HELVETICA_BOLD_MISSING_WIDTH = 667
const HELVETICA_BOLD_CHAR_WIDTHS: Record<string, number> = {
  ' ': 278,
  "'": 278,
  '-': 333,
  A: 722,
  B: 667,
  C: 722,
  D: 722,
  E: 667,
  F: 611,
  G: 778,
  H: 722,
  I: 278,
  J: 556,
  K: 722,
  L: 611,
  M: 833,
  N: 722,
  O: 778,
  P: 667,
  Q: 778,
  R: 722,
  S: 667,
  T: 611,
  U: 722,
  V: 667,
  W: 944,
  X: 667,
  Y: 667,
  Z: 611
}

export const certificateNameMaxWidth = (pageWidth = PDF_PAGE_LONG_SIDE): number =>
  pageWidth * PDF_NAME_MAX_WIDTH_PCT / 100

export const measureHelveticaBoldWidth: MeasureTextWidth = (text, fontSize) => {
  let units = 0
  for (const character of text) {
    units += HELVETICA_BOLD_CHAR_WIDTHS[character] ?? HELVETICA_BOLD_MISSING_WIDTH
  }
  return units / 1000 * fontSize
}

export const fitCertificateNameFontSize = (
  name: string,
  options: FitCertificateNameOptions = {}
): number => {
  if (name.length === 0) {
    return PDF_NAME_FONT_SIZE
  }

  const pageWidth = options.pageWidth ?? PDF_PAGE_LONG_SIDE
  const measureWidth = options.measureWidth ?? measureHelveticaBoldWidth
  const maxWidth = certificateNameMaxWidth(pageWidth)
  const widthAtDefault = measureWidth(name, PDF_NAME_FONT_SIZE)
  if (widthAtDefault <= maxWidth) {
    return PDF_NAME_FONT_SIZE
  }

  return Math.max(
    PDF_NAME_MIN_FONT_SIZE,
    PDF_NAME_FONT_SIZE * maxWidth / widthAtDefault
  )
}

export const certificateNameNeedsWrap = (
  name: string,
  fontSize: number,
  options: FitCertificateNameOptions = {}
): boolean => {
  if (name.length === 0 || fontSize > PDF_NAME_MIN_FONT_SIZE) {
    return false
  }

  const pageWidth = options.pageWidth ?? PDF_PAGE_LONG_SIDE
  const measureWidth = options.measureWidth ?? measureHelveticaBoldWidth
  return measureWidth(name, fontSize) > certificateNameMaxWidth(pageWidth)
}

export const CERT_BACKGROUND_URLS = {
  enterprise: {
    2025: '/assets/images/Completion_Cert_BLANK.jpg',
    2026: '/assets/images/Completion_Cert_BLANK_2026.jpg'
  },
  d2c: {
    2025: '/assets/images/Completion_Cert_BLANK_D2C.jpg',
    2026: '/assets/images/Completion_Cert_BLANK_D2C_2026.jpg'
  }
} as const

export const DEV_CERTIFICATE_DEFAULTS = {
  firstName: 'Jane',
  lastName: 'Rescuer',
  institutionName: 'Whittier College',
  referenceNumber: 'DEV-0001',
  variant: 'enterprise' as CertificateVariant
}

export const DEV_SAMPLE_DATE_2025 = '2025-12-15'
export const DEV_SAMPLE_DATE_2026 = '2026-01-15'

export const isCertificateDevToolEnabled = (): boolean =>
  config.environment === Environment.DEVELOPMENT || config.environment === Environment.STAGE

export const isCertificateDevToolAuthRequired = (): boolean =>
  config.environment === Environment.STAGE

export interface CertificateInput {
  firstName: string
  lastName: string
  institutionName?: string
  completedAt: Date
  referenceNumber: string
  variant: CertificateVariant
}

export interface CertificateFields {
  firstName?: string
  lastName?: string
  institutionName?: string
  endedAt?: Date | string
  referenceNumber?: string
  variant?: CertificateVariant
}

export interface OverlayPoint {
  xPct: number
  yPct: number
}

export interface CertificateOverlayLayout {
  name: OverlayPoint
  institution: OverlayPoint
  date: OverlayPoint
  reference: OverlayPoint
}

// jsPDF anchors text on its baseline while the HTML preview anchors on the vertical
// center of the line. This is the baseline drop from that center as a fraction of
// font size — roughly half the cap height of Helvetica.
const BASELINE_DROP_PER_FONT_SIZE = 0.32

// yPct is the vertical center of each field, so preview and PDF agree. The 2025
// values are set so the PDF baselines come out where the 2025 certificate has
// always drawn them (name 190, institution 292, date and reference 400).
const LAYOUT_2025: CertificateOverlayLayout = {
  name: { xPct: 50, yPct: 28.54 },
  institution: { xPct: 50, yPct: 46.56 },
  date: { xPct: 31.7, yPct: 63.9 },
  reference: { xPct: 69.9, yPct: 63.9 }
}

// The 2026 artwork bakes in a header and body copy, so every field sits lower.
// x values are the centers of the two signature rules.
const LAYOUT_2026: CertificateOverlayLayout = {
  name: { xPct: 50, yPct: 33 },
  institution: { xPct: 50, yPct: 51.5 },
  date: { xPct: 31.7, yPct: 71.3 },
  reference: { xPct: 69.9, yPct: 71.3 }
}

export const getCertificateOverlayLayout = (year: CertificateYear): CertificateOverlayLayout =>
  year === 2026 ? LAYOUT_2026 : LAYOUT_2025

export const overlayPctToPdf = (pct: number, pageSize: number, fontSize = 0): number =>
  pct / 100 * pageSize + fontSize * BASELINE_DROP_PER_FONT_SIZE

export const getCertificateYear = (completedAt: Date): CertificateYear =>
  completedAt.getTime() >= CERT_2026_CUTOFF_MS ? 2026 : 2025

export const getOverlayColor = (year: CertificateYear): [number, number, number] =>
  year === 2026 ? OVERLAY_DARK_GRAY : OVERLAY_NAVY

export const overlayColorToCss = (color: [number, number, number]): string =>
  `rgb(${color[0]}, ${color[1]}, ${color[2]})`

export const getCertificateBackgroundUrl = (
  variant: CertificateVariant,
  year: CertificateYear
): string => CERT_BACKGROUND_URLS[variant][year]

export const getCertificateVariant = (): CertificateVariant =>
  hasUrlFeatureFlagTrain() ? 'd2c' : 'enterprise'

/**
 * Rendered in the viewer's local timezone, which is how every certificate issued so
 * far was drawn — re-downloading an old certificate must not change its printed date.
 * Choosing the 2026 artwork is a separate decision and compares the UTC instant.
 */
export const formatCertificateDate = (completedAt: Date): string =>
  completedAt.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })

export const buildCertificateInputFromFields = (fields: CertificateFields): CertificateInput | null => {
  const { firstName, lastName, institutionName, endedAt, referenceNumber } = fields
  const variant = fields.variant ?? getCertificateVariant()

  if (firstName == null || lastName == null ||
      endedAt == null || referenceNumber == null || referenceNumber.length === 0) {
    return null
  }

  // Only the enterprise certificate draws an institution, so a D2C student does not
  // have to wait on the account fetch before their certificate can be built.
  if (variant === 'enterprise' && institutionName == null) {
    return null
  }

  return {
    firstName,
    lastName,
    institutionName,
    completedAt: new Date(endedAt),
    referenceNumber,
    variant
  }
}

export const buildCertificateInput = (
  userProfile: UserProfile | null,
  institution: Institution | null,
  currentAttempt: Attempt | null,
  variantOverride?: CertificateVariant
): CertificateInput | null => buildCertificateInputFromFields({
  firstName: userProfile?.first_name,
  lastName: userProfile?.last_name,
  institutionName: institution?.name,
  endedAt: currentAttempt?.endedAt,
  referenceNumber: currentAttempt?.referenceNumber,
  variant: variantOverride
})

/** Identifies the certificate an input produces, so a cached PDF is only reused for the same one. */
export const certificateInputKey = (input: CertificateInput): string => [
  input.firstName,
  input.lastName,
  input.institutionName ?? '',
  input.completedAt.getTime(),
  input.referenceNumber,
  input.variant
].join('|')
